<script>
  import { pluralize } from 'journalize';
  export let feed;
  export let level;

  $: totalSeats = $feed?.total ?? 0;

  $: othersCaucusWithDem = $feed?.othersCaucusWithDem ?? null;

  $: othersCaucusWithGop = $feed?.othersCaucusWithGop ?? null;

  $: demHoldovers = $feed?.dem?.holdovers ?? 0;
  $: gopHoldovers = $feed?.gop?.holdovers ?? 0;
  $: othersHoldovers = $feed?.others?.holdovers ?? 0;

  $: fixedDem =
    demHoldovers +
    (othersCaucusWithDem?.holdovers ?? 0);

  $: fixedGop =
    gopHoldovers +
    (othersCaucusWithGop?.holdovers ?? 0);

  $: seatsUpForElection =
    totalSeats - fixedDem - fixedGop - othersHoldovers;

  // Current already includes holdovers.
  // These values determine the fixed border assignments.
  $: currentDem =
    ($feed?.dem?.current ?? 0) +
    (othersCaucusWithDem?.current ?? 0);

  $: currentGop =
    ($feed?.gop?.current ?? 0) +
    (othersCaucusWithGop?.current ?? 0);

  $: currentOthers =
    $feed?.others?.current ?? 0;

  // Big numbers: declared winners + holdovers.
  $: totalDems =
    ($feed?.dem?.won ?? 0) + demHoldovers;

  $: totalGop =
    ($feed?.gop?.won ?? 0) + gopHoldovers;

  $: totalCaucusWithDems =
    (othersCaucusWithDem?.won ?? 0) +
    (othersCaucusWithDem?.holdovers ?? 0);

  $: totalCaucusWithGop =
    (othersCaucusWithGop?.won ?? 0) +
    (othersCaucusWithGop?.holdovers ?? 0);

  $: totalDemSeats = totalDems + totalCaucusWithDems;
  $: totalGopSeats = totalGop + totalCaucusWithGop;

  $: totalOther =
    ($feed?.others?.won ?? 0) + othersHoldovers;

  // Declared winners determine solid fills.
  $: demWon =
    ($feed?.dem?.won ?? 0) +
    (othersCaucusWithDem?.won ?? 0);

  $: gopWon =
    ($feed?.gop?.won ?? 0) +
    (othersCaucusWithGop?.won ?? 0);

  $: othersWon = $feed?.others?.won ?? 0;
  $: hasOther = totalOther > 0;

  $: demNetChange =
    ($feed?.dem?.netWinners ?? 0) + (othersCaucusWithDem?.netWinners ?? 0);

  $: gopNetChange =
    ($feed?.gop?.netWinners ?? 0) + (othersCaucusWithGop?.netWinners ?? 0);

  $: othersNetChange = $feed?.others?.netWinners ?? 0;

  $: uncalled = Math.max(
    0,
    seatsUpForElection - demWon - gopWon - othersWon
  );

  $: netChanges = {
    dem: demNetChange,
    gop: gopNetChange
  };

  // One string so screen readers don't split it into "seat" and "s"
  const netChangeText = (/** @type {number} */ change) =>
    `${change > 0 ? 'Gained' : 'Lost'} ${Math.abs(change)} seat${pluralize(
      Math.abs(change)
    )}`;

  // Screen reader note for the seat chart and legends, which are aria-hidden.
  // Party totals are read from the visible numbers instead.
  $: holdoverCounts = [
    fixedDem && `${fixedDem} Democratic`,
    fixedGop && `${fixedGop} Republican`,
    othersHoldovers && `${othersHoldovers} other`
  ].filter(Boolean);

  $: holdoverList =
    holdoverCounts.length > 1
      ? `${holdoverCounts.slice(0, -1).join(', ')} and ${holdoverCounts[holdoverCounts.length - 1]}`
      : holdoverCounts[0];

  $: holdoverTotal = fixedDem + fixedGop + othersHoldovers;

  $: chartNote = [
    uncalled
      ? `${uncalled} race${pluralize(uncalled)} ${uncalled === 1 ? 'is' : 'are'} uncalled.`
      : 'All races have been called.',
    holdoverList
      ? `${holdoverList} seat${pluralize(holdoverTotal)} ${
          holdoverTotal === 1 ? 'is' : 'are'
        } not up for election this year.`
      : ''
  ]
    .filter(Boolean)
    .join(' ');

  // Fixed positions for seats not up for election.
  $: holdoversClass = (/** @type {number} */ n) => {
    if (!['senate', 'governor'].includes(level) || !totalSeats) {
      return '';
    }

    if (n <= fixedDem) return 'no-contest-dem';
    if (n > totalSeats - fixedGop) return 'no-contest-gop';
    if (n <= fixedDem + othersHoldovers) return 'no-contest-other';

    return '';
  };

  // Election results affect fills only.
  $: fillClass = (/** @type {number} */ n) => {
    if (!totalSeats) return 'undecided';

    if (level === 'senate' || level === 'governor') {
      if (n <= fixedDem) return 'dem';
      if (n > totalSeats - fixedGop) return 'gop';
      if (n <= fixedDem + othersHoldovers) return 'other';

      const contestedIndex = n - fixedDem - othersHoldovers;

      if (contestedIndex <= demWon) return 'dem';

      if (contestedIndex <= demWon + othersWon) {
        return 'other';
      }

      if (contestedIndex > seatsUpForElection - gopWon) {
        return 'gop';
      }

      return 'undecided';
    }

    if (n <= demWon) return 'dem';
    if (n <= demWon + othersWon) return 'other';
    if (n > totalSeats - gopWon) return 'gop';

    return 'undecided';
  };

  // Border assignments depend only on current seats and holdovers.
  // No won, leading, or winningTrend values are used here.
  $: borderClass = (/** @type {number} */ n) => {
    if (!totalSeats) return '';

    if (level === 'senate' || level === 'governor') {
      if (holdoversClass(n)) return '';

      const contestedIndex = n - fixedDem - othersHoldovers;
      const contestedDem = currentDem - fixedDem;
      const contestedOthers = currentOthers - othersHoldovers;

      if (contestedIndex <= contestedDem) return 'is_dem';

      if (contestedIndex <= contestedDem + contestedOthers) {
        return 'is_other';
      }

      if (
        contestedIndex >
        seatsUpForElection - (currentGop - fixedGop)
      ) {
        return 'is_gop';
      }

      return '';
    }

    if (n <= currentDem) return 'is_dem';
    if (n <= currentDem + currentOthers) return 'is_other';

    return 'is_gop';
  };

</script>

<div class="election-trends">
  <div
    class="election-trends__results"
    class:election-trends__results--with-other={hasOther}
  >
    <!-- Democrats -->
    <div class="election-trends__party">
      <div class="election-trends__party-name text-2xl">
        <span class="party-name-desktop">Democrats</span>
        <span class="party-name-mobile" aria-hidden="true">DEM</span>
      </div>

      <div class="election-trends__seat-count text-5xl color-dem">
        {totalDemSeats}<span class="visually-hidden"
          >{` seat${pluralize(totalDemSeats)}.`}</span
        >
      </div>

      <div class="election-trends__net-change text-sm color-dark-grey">
        {#if demNetChange !== 0}
          {netChangeText(demNetChange)}
        {/if}
      </div>
    </div>

    <!-- Other -->
    {#if hasOther}
      <div class="election-trends__party election-trends__party--other">
        <div class="election-trends__party-name text-2xl">
          Other
        </div>

        <div class="election-trends__seat-count text-5xl trends-other">
          {totalOther}<span class="visually-hidden"
            >{` seat${pluralize(totalOther)}.`}</span
          >
        </div>

        <div class="election-trends__net-change text-sm color-dark-grey">
          {#if othersNetChange !== 0}
            {netChangeText(othersNetChange)}
          {/if}
        </div>
      </div>
    {/if}

    <!-- Republicans -->
    <div class="election-trends__party election-trends__party--gop">
      <div class="election-trends__party-name text-2xl">
        <span class="party-name-desktop">Republicans</span>
        <span class="party-name-mobile" aria-hidden="true">GOP</span>
      </div>

      <div class="election-trends__seat-count text-5xl color-gop">
        {totalGopSeats}<span class="visually-hidden"
          >{` seat${pluralize(totalGopSeats)}.`}</span
        >
      </div>

      <div class="election-trends__net-change text-sm color-dark-grey">
        {#if gopNetChange !== 0}
          {netChangeText(gopNetChange)}
        {/if}
      </div>
    </div>
  </div>
</div>

<div
  class="election-trends__chart"
  class:election-trends__senate-chart={level === 'senate'}
>
  <!-- A div, not a p: the HP embed hides `.election-trends__chart > p` -->
  {#if totalSeats}
    <div class="visually-hidden">{chartNote}</div>
  {/if}

  <slot
    {fillClass}
    {borderClass}
    {holdoversClass}
    {netChanges}
    {uncalled}
  />

  <p class="text-sans-serif text-xs text-regular color-grey">
    Party totals may include members of other parties caucusing with Democrats or Republicans.
  </p>
</div>